import React from 'react';
import { FileText, Download, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { studyDownloadUrl } from '../lib/content';

export function fmtSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

export function StudyCard({ s, copy, label }) {
  return (
    <article data-testid={`study-card-${s.id}`} className="card-rise bg-white border border-[#e5dcc5] rounded-2xl p-7 flex flex-col">
      {label && <span className="self-start text-[10px] uppercase tracking-wider-xtra px-2.5 py-1 rounded-full bg-[#1f3a2a] text-[#d4a63d] font-medium mb-4">{label}</span>}
      <h3 className="font-serif-display text-xl text-[#1f3a2a] leading-snug">{s.title}</h3>
      {s.description && <p className="mt-2 text-[#4a5a4e] text-sm leading-relaxed line-clamp-3">{s.description}</p>}
      <div className="mt-auto pt-4 flex items-center gap-2 text-xs text-[#8a7a4e]"><FileText size={13} /> PDF · {fmtSize(s.size)}</div>
      <div className="mt-4 pt-4 border-t border-[#f0e9d8]">
        <Button asChild className="bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] rounded-full h-10 px-4">
          <a href={studyDownloadUrl(s.id)} target="_blank" rel="noreferrer" data-testid={`study-open-${s.id}`} className="inline-flex items-center gap-2"><Download size={15} /> {copy.open}</a>
        </Button>
      </div>
    </article>
  );
}

export function ComingSoonCard({ n, copy }) {
  return (
    <article data-testid={`study-coming-soon-${n}`} className="rounded-2xl p-7 border border-dashed border-[#c9bd9c] bg-[#f5efe3]/60 flex flex-col">
      <span className="self-start text-[10px] uppercase tracking-wider-xtra px-2.5 py-1 rounded-full bg-[#e5dcc5] text-[#8a7a4e] font-medium mb-4">{copy.session} {n}</span>
      <h3 className="font-serif-display text-xl text-[#8a7a4e] leading-snug inline-flex items-center gap-2"><Lock size={16} /> {copy.soon}</h3>
      <p className="mt-2 text-[#8a7a4e] text-sm leading-relaxed">{copy.soonText}</p>
    </article>
  );
}

export function SeriesBlock({ name, total, items, copy }) {
  const bySession = Object.fromEntries(items.map((s) => [s.session, s]));
  const count = Math.max(total || 0, ...items.map((s) => s.session));
  const nums = Array.from({ length: count }, (_, i) => i + 1);
  const [title, subtitle] = name.split(' · ');
  return (
    <div data-testid="study-series" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">{copy.series} · {items.length}/{count} {copy.available}</span>
          <h3 className="font-serif-display text-3xl md:text-4xl text-[#1f3a2a] mt-2">{title}{subtitle && <span className="italic text-[#2e5340]"> — {subtitle}</span>}</h3>
        </div>
        <ol className="flex items-center" aria-label={copy.progress} data-testid="study-series-progress">
          {nums.map((n, i) => (
            <li key={n} className="flex items-center">
              {i > 0 && <span className={`w-6 sm:w-10 h-px ${bySession[n] ? 'bg-[#1f3a2a]' : 'bg-[#c9bd9c]'}`} />}
              <span className={`w-8 h-8 rounded-full text-xs font-medium flex items-center justify-center ${bySession[n] ? 'bg-[#1f3a2a] text-[#d4a63d]' : 'border border-dashed border-[#c9bd9c] text-[#8a7a4e]'}`}>{n}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className={`mt-8 grid md:grid-cols-2 ${count >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-5`}>
        {nums.map((n) => (bySession[n]
          ? <StudyCard key={n} s={bySession[n]} copy={copy} label={`${copy.session} ${n}`} />
          : <ComingSoonCard key={n} n={n} copy={copy} />))}
      </div>
    </div>
  );
}
